import { Subscription } from 'rxjs';
import { FormGroup, FormControl, Validators, FormArray } from '@angular/forms';

import { BocOpt, FastLaneRequest, FastLaneItem } from './fastlane.model';

class FormProduct extends FormGroup {
	readonly product: FastLaneItem;
	constructor(product: FastLaneItem) {
		super({

			steelGrade: new FormControl(
				product.steelgrade,
				Validators.required
			),
		});
		this.product = product;
	}

	getFormSteelGrade() {
		return <FormControl> this.get('steelgrade');
	}
}

class FormProducts extends FormArray {
	private readonly subscription: Subscription | undefined;
	private readonly parentProducts: FormProducts | undefined;
	constructor(parentProducts?: FormProducts) {
		super([], Validators.minLength(1));
		this.parentProducts = parentProducts;
		if (!this.parentProducts) {
			return;
		}
		this.subscription = this.valueChanges.subscribe(() => {
			if (this.parentProducts) {
				this.parentProducts.updateValueAndValidity();
			}
		});
	}

	isValid() {
		if (this.length === 0) {
			return false;
		}
		for (let i = 0; i < this.length; i++) {
			if (this.getFormProductAt(i).invalid) {
				return false;
			}
		}
		return true;
	}

	getFormProductAt(index: number) {
		return <FormProduct> this.at(index);
	}

	getFormProducts() {
		return this.controls.map(control => <FormProduct> control);
	}

	onDestroy() {
		if (this.subscription) {
			this.subscription.unsubscribe();
		}
	}
}

export class FormFastLane extends FormGroup {
	private lastSoldTo: string | undefined;
	private lastShipTo: string | undefined;

	constructor() {
		super({
			customerRequestName: new FormControl(undefined, Validators.compose([
				Validators.required,
				Validators.maxLength(120)
			])),
			description: new FormControl(undefined),
			soldTo: new FormControl(undefined, Validators.required),
			shipTo: new FormControl(undefined, Validators.required),
			shipToCustom: new FormControl(undefined),
			shipToPerson: new FormControl(undefined),
			shipToContact: new FormControl(undefined),
			currency: new FormControl(undefined, Validators.required),
			idd: new FormControl(undefined, Validators.required),
			products: new FormProducts(),
			fastLaneAttachments: new FormArray([]),
			attachmentErrorMessages: new FormControl('', { validators: [] })
		});
	}

	getFormCustomerRequestName() {
		return <FormControl> this.get('customerRequestName');
	}

	getFormDescription() {
		return <FormControl> this.get('description');
	}

	getFastLaneRequestAttachments() {
		return <FormArray> this.get('fastLaneAttachments');
	}

	getFormSoldTo() {
		return <FormControl> this.get('soldTo');
	}

	getFormShipToCustom() {
		return <FormControl> this.get('shipToCustom');
	}

	getFormShipToPerson() {
		return <FormControl> this.get('shipToPerson');
	}

	getFormShipToContact() {
		return <FormControl> this.get('shipToContact');
	}

	getFormShipTo() {
		return <FormControl> this.get('shipTo');
	}

	getFormCurrency() {
		return <FormControl> this.get('currency');
	}

	getFormPricingUnit() {
		return <FormControl> this.get('pricingUnit');
	}

	getFormProducts() {
		return <FormProducts> this.get('products');
	}

	isFastLaneHeaderValid() {
		return this.getFormCustomerRequestName().valid
			&& this.getFormSoldTo().valid
			&& this.getFormShipTo().valid;
	}

	getAndUpdateLastBoc(newBoc: BocOpt) {
		this.lastSoldTo = newBoc.soldTo;
		const soldTo = this.lastSoldTo;
		this.lastShipTo = newBoc.shipTo;
		const shipTo = this.lastShipTo;
		return { soldTo, shipTo };
	}

	setFastLane(fastLaneRequest: FastLaneRequest) {
		this.getFormCustomerRequestName().setValue(fastLaneRequest.requestName);
		this.getFormDescription().setValue(fastLaneRequest.description);
		this.getFormCurrency().setValue(fastLaneRequest.currency);
		this.getFormSoldTo().setValue(fastLaneRequest.soldTo);
		this.getFormShipTo().setValue(fastLaneRequest.shipTo);
		return this;
	}
}
